import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Modal, TextField, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { Close } from '@mui/icons-material';
import { product } from '../api/paquetes';
import { useNotification } from '../context/notification.context';

const ExchangeRateWidget: React.FC = () => {
    const [rate, setRate] = useState<number | null>(null);
    const [history, setHistory] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const [view, setView] = useState<'edit' | 'history'>('edit');
    const [newValue, setNewValue] = useState('');
    const { getSuccess, getError } = useNotification();

    const fetchRate = useCallback(async () => {
        try {
            const res = await product.getTipoCambioHoy();
            if (res.data && res.data.cotizacion) {
                setRate(res.data.cotizacion);
            }
        } catch (error) {
            console.error("Error fetching rate", error);
        }
    }, []);

    const fetchHistory = async () => {
        try {
            const res = await product.getTipoCambioHistory();
            if (res.data) setHistory(res.data);
        } catch (error) {
            console.error("Error fetching history", error);
        }
    };

    useEffect(() => {
        fetchRate();
        const interval = setInterval(fetchRate, 1000 * 60 * 10);
        return () => clearInterval(interval);
    }, [fetchRate]);

    useEffect(() => {
        if (open && view === 'history') fetchHistory();
    }, [open, view]);

    const handleOpen = () => {
        setView('edit');
        if (rate) setNewValue(rate.toString());
        setOpen(true);
    };

    const handleSave = async () => {
        try {
            const val = parseFloat(newValue);
            if (isNaN(val) || val <= 0) {
                getError("Ingrese un valor válido");
                return;
            }
            await product.updateTipoCambio(val);
            setRate(val);
            getSuccess("Tipo de cambio actualizado");
            setOpen(false);
        } catch (error) {
            getError("Error al actualizar tipo de cambio");
        }
    };

    return (
        <>
            <Paper 
                elevation={0}
                onClick={handleOpen}
                sx={{ 
                    padding: '4px 12px', 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    lineHeight: 1,
                    '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.25)'
                    }
                }}
            >
                <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.3 }}>
                    Cambio del día
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                    {rate ? `$ ${Number(rate).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                </Typography>
            </Paper>

            <Modal open={open} onClose={() => setOpen(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 450,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6">
                            {view === 'edit' ? 'Cambio del día' : 'Historial de Moneda'}
                        </Typography>
                        <IconButton onClick={() => setOpen(false)}><Close /></IconButton>
                    </Box>

                    {view === 'edit' ? (
                        <>
                            <TextField
                                fullWidth
                                label="U$S Dolar"
                                type="number"
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                                variant="outlined"
                                sx={{ mb: 3 }}
                            />
                            <Button 
                                fullWidth 
                                variant="contained" 
                                color="success" 
                                onClick={handleSave}
                                sx={{ py: 1.5, mb: 2 }}
                            >
                                Guardar
                            </Button>
                            <Button 
                                fullWidth 
                                variant="outlined" 
                                onClick={() => setView('history')}
                                sx={{ py: 1 }}
                            >
                                Ver Historial Moneda
                            </Button>
                        </>
                    ) : (
                        <>
                            <Box sx={{ 
                                maxHeight: 350, 
                                overflow: 'auto', 
                                border: '1px solid #eee', 
                                borderRadius: 1 
                            }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableRow>
                                            <TableCell><b>Fecha</b></TableCell>
                                            <TableCell align="right"><b>Valor</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.length > 0 ? (
                                            history.map((h) => (
                                                <TableRow key={h.id}>
                                                    <TableCell>{new Date(h.fecha).toLocaleDateString()}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                        $ {Number(h.cotizacion).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">No hay historial disponible</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                            <Button 
                                fullWidth 
                                sx={{ mt: 3 }} 
                                onClick={() => setView('edit')}
                            >
                                Volver a Edición
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default ExchangeRateWidget;
